// App chrome: top bar (Arlo, clock, who's logging, sync, emergency) and
// navigation — a side rail on tablets, a bottom bar on phones.

import type { LucideIcon } from 'lucide-react';
import {
  BookHeart,
  BookOpen,
  Cctv,
  MessageCircleQuestion,
  Footprints,
  HeartHandshake,
  ChartColumn,
  Cloud,
  CloudOff,
  House,
  HeartPulse,
  GraduationCap,
  List,
  Menu,
  Mic,
  Power,
  RefreshCw,
  Settings,
  ShoppingBasket,
  Siren,
  Sparkles,
  Utensils,
  Wallet,
} from 'lucide-react';
import { Fragment, useState, type ReactNode } from 'react';
import { openVoice } from '../features/voice/VoiceAssistant.tsx';
import { isSession, setDevicePrefs } from '../data/device.ts';
import { checkKey } from '../data/api.ts';
import { reconnect } from '../data/index.ts';
import { GoogleSignIn } from '../features/settings/GoogleSignIn.tsx';
import { useRole } from '../data/role.ts';
import { useCollection, useDevice, useHousehold, useLiveStatus, useNow, useProfile, useSyncState } from '../data/hooks.ts';
import { stageFor } from '../domain/age.ts';
import { Avatar } from '../ui/bits.tsx';
import { useWeather } from '../data/live.ts';
import { describeCode } from '../domain/weather.ts';
import { Sheet } from '../ui/primitives.tsx';
import { navigate, useRoute } from './router.ts';
import { useServerBuild } from './build.ts';
import { setScreenOff } from './kiosk.ts';

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

export const NAV: NavItem[] = [
  { path: '/', label: 'Today', icon: House },
  { path: '/timeline', label: 'Timeline', icon: List },
  { path: '/walks', label: 'Walks', icon: Footprints },
  { path: '/insights', label: 'Insights', icon: ChartColumn },
  { path: '/diary', label: 'Diary', icon: BookHeart },
  { path: '/health', label: 'Health', icon: HeartPulse },
  { path: '/food', label: 'Food', icon: Utensils },
  { path: '/training', label: 'Training', icon: GraduationCap },
  { path: '/social', label: 'Socialise', icon: Sparkles },
  { path: '/ask', label: 'Ask', icon: MessageCircleQuestion },
  { path: '/camera', label: 'Camera', icon: Cctv },
  { path: '/guide', label: 'Guide', icon: BookOpen },
  { path: '/shopping', label: 'Shopping', icon: ShoppingBasket },
  { path: '/costs', label: 'Costs', icon: Wallet },
  { path: '/settings', label: 'Settings', icon: Settings },
];

// Phone bottom bar: two items, the big mic, one item, More.
const PHONE_NAV = ['/', '/walks', '/health'];

// A sitter's device: the essentials only.
const SITTER_ITEM: NavItem = { path: '/sitter', label: 'Sitter guide', icon: HeartHandshake };
const SITTER_NAV = ['/', '/sitter', '/timeline', '/walks', '/camera', '/ask', '/diary', '/guide'];
const SITTER_PHONE = ['/', '/sitter', '/camera'];

function useNav() {
  const d = useDevice();
  if (!d.guest) return { nav: NAV, phone: PHONE_NAV };
  const nav = [NAV[0], SITTER_ITEM, ...NAV.slice(1)].filter((n) => SITTER_NAV.includes(n.path));
  return { nav, phone: SITTER_PHONE };
}

function isActive(item: string, path: string) {
  return item === '/' ? path === '/' : path === item || path.startsWith(item + '/');
}

export function Shell({ children }: { children: ReactNode }) {
  const route = useRoute();
  const [more, setMore] = useState(false);
  const { nav: NAV, phone: PHONE_NAV } = useNav();
  useRole();
  // Tablet/desktop rail: Ask becomes the big orb in the middle, like the phone's bottom bar.
  const railItems = NAV.filter((n) => n.path !== '/ask');
  const railMid = Math.floor(railItems.length / 2);
  return (
    <div className="shell">
      <TopBar />
      <nav className="rail" aria-label="Main">
        {railItems.map((n, i) => (
          <Fragment key={n.path}>
            {i === railMid && <RailAsk active={isActive('/ask', route.path)} />}
            <a href={`#${n.path}`} className={isActive(n.path, route.path) ? 'on' : ''} aria-current={isActive(n.path, route.path) ? 'page' : undefined}>
              <n.icon aria-hidden />
              <span>{n.label}</span>
            </a>
          </Fragment>
        ))}
      </nav>
      <main className="main" id="main">
        <NotSavedWarning />
        <UpdateBanner />
        {children}
      </main>
      <nav className="bottombar" aria-label="Main">
        {NAV.filter((n) => PHONE_NAV.includes(n.path)).map((n, i) => (
          <Fragment key={n.path}>
            {i === 2 && <MicTab />}
            <a href={`#${n.path}`} className={isActive(n.path, route.path) ? 'on' : ''}>
              <n.icon aria-hidden />
              <span>{n.label}</span>
            </a>
          </Fragment>
        ))}
        <button type="button" onClick={() => setMore(true)} className={NAV.some((n) => !PHONE_NAV.includes(n.path) && isActive(n.path, route.path)) ? 'on' : ''}>
          <Menu aria-hidden />
          <span>More</span>
        </button>
      </nav>
      <Sheet open={more} onClose={() => setMore(false)} title="More">
        <div className="more-grid">
          {NAV.filter((n) => !PHONE_NAV.includes(n.path)).map((n) => (
            <a key={n.path} href={`#${n.path}`} onClick={() => setMore(false)} className="more-item">
              <n.icon aria-hidden />
              <span>{n.label}</span>
            </a>
          ))}
          <a href="#/emergency" onClick={() => setMore(false)} className="more-item danger">
            <Siren aria-hidden />
            <span>Emergency</span>
          </a>
        </div>
      </Sheet>
    </div>
  );
}

function RailAsk({ active }: { active: boolean }) {
  return (
    <button type="button" className={`rail-ask${active ? ' on' : ''}`} onClick={() => openVoice({ listen: true })} aria-label="Talk to Arlo HQ">
      <span className="rail-ask-orb">
        <Mic aria-hidden />
      </span>
      <span>Ask</span>
    </button>
  );
}

function MicTab() {
  return (
    <button type="button" className="mic-tab" onClick={() => openVoice({ listen: true })} aria-label="Talk to Arlo HQ">
      <span className="mic-tab-orb">
        <Mic aria-hidden />
      </span>
      <span>Ask</span>
    </button>
  );
}

function TopBar() {
  const profile = useProfile();
  const kiosk = useDevice().kiosk;
  const status = useLiveStatus(60_000);
  const now = useNow(15_000);
  const stage = status.age ? stageFor(status.age.weeks) : undefined;
  const forecast = useWeather();
  const weatherNow = forecast?.hours.find((h) => h.time <= now && h.time > now - 3_600_000) ?? forecast?.hours.find((h) => h.time > now);
  return (
    <header className="topbar">
      <a href="#/" className="topbar-arlo" aria-label="Today">
        <span className="arlo-avatar">{profile.photo ? <img src={profile.photo} alt="" /> : <img src="./arlo.jpg" alt="" />}</span>
        <span className="topbar-name">
          <strong>{profile.name}</strong>
          <span className="muted small">{status.age ? `${status.age.label}${stage ? ` · ${stage.title}` : ''}` : profile.breed}</span>
        </span>
      </a>
      <div className="topbar-clock" aria-hidden>
        <span className="clock-time">{new Date(now).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
        <span className="clock-date">
          {new Date(now).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
          {weatherNow && ` · ${Math.round(weatherNow.tempC)}° ${describeCode(weatherNow.code).toLowerCase()}${weatherNow.rainChance >= 40 ? ` · ${weatherNow.rainChance}% rain` : ''}`}
        </span>
      </div>
      <div className="topbar-right">
        {kiosk && (
          <button type="button" className="power-btn" onClick={() => setScreenOff(true)} aria-label="Turn the screen off" title="Screen off">
            <Power aria-hidden />
          </button>
        )}
        <SyncDot />
        <PersonSwitch />
        <button type="button" className="sos" onClick={() => navigate('/emergency')}>
          <Siren aria-hidden />
          <span>Emergency</span>
        </button>
      </div>
    </header>
  );
}

/** A server exists but this device isn't saving to it (no key, or a rejected one). */
function useNotSaved(): boolean {
  const s = useSyncState();
  const d = useDevice();
  return !d.guest && (s.kind === 'needs-key' || s.kind === 'error');
}

/**
 * Loud, on every screen while a device isn't saving to the server — with the
 * fix right there (key, or Google on a phone), not a trip to Settings.
 */
function NotSavedWarning() {
  const s = useSyncState();
  const d = useDevice();
  const route = useRoute();
  const notSaved = useNotSaved();
  const [key, setKey] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  if (!notSaved || route.path.startsWith('/settings/sync')) return null;
  const thing = d.kiosk ? 'tablet' : 'device';
  const title =
    s.kind === 'error'
      ? isSession(d.key)
        ? 'Your Google sign-in has ended — sign in again'
        : `This ${thing}’s household key isn’t working`
      : `This ${thing} isn’t saving to the server`;
  const connect = async () => {
    if (!key.trim()) return setError('Type the household key first.');
    setBusy(true);
    const result = await checkKey(key);
    setBusy(false);
    if (result === 'wrong') return setError('That key wasn’t accepted — check for typos (it’s case-sensitive, and spaces count).');
    if (result === 'guest') return setError('That’s a sitter link, not the household key.');
    if (result === 'no-server') return setError('Couldn’t reach the server — check the internet connection.');
    setError('');
    setDevicePrefs({ key: key.trim() });
    await reconnect();
  };
  return (
    <div className="callout tone-bad not-saved" role="alert">
      <CloudOff aria-hidden />
      <div>
        <strong>{title}</strong>
        <span className="small">What’s logged here is only on this {thing} until it’s connected — then it all uploads straight away.</span>
        <form
          className="not-saved-form"
          onSubmit={(e) => {
            e.preventDefault();
            void connect();
          }}
        >
          <input className="input" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Household key" aria-label="Household key" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          <button type="submit" className="btn btn-primary btn-md" disabled={busy}>
            <span>{busy ? 'Checking…' : 'Connect'}</span>
          </button>
        </form>
        {error && <span className="small not-saved-error">{error}</span>}
        {!d.kiosk && <GoogleSignIn lead="Or:" />}
        {d.kiosk && (
          <span className="small muted">
            So it stays connected after a reset, put the key in Scalefusion’s launch URL too: <code>{`${location.origin}/?kiosk=1&key=YOUR-KEY`}</code>
          </span>
        )}
      </div>
    </div>
  );
}

/** Phones and laptops: a quiet nudge when a newer version has been deployed. */
function UpdateBanner() {
  const { stale } = useServerBuild();
  const d = useDevice();
  if (!stale || d.kiosk) return null;
  return (
    <button type="button" className="callout tone-info update-banner" onClick={() => location.reload()}>
      <RefreshCw aria-hidden />
      <div>
        <strong>A new version of Arlo HQ is ready</strong>
        <span className="small">Tap to update — takes a second.</span>
      </div>
    </button>
  );
}

function SyncDot() {
  const s = useSyncState();
  // The red banner says it (and fixes it); a second, vaguer badge only confuses.
  if (useNotSaved()) return null;
  let icon = Cloud;
  let label = 'Synced';
  let tone = 'good';
  if (s.kind === 'local-only') {
    icon = CloudOff;
    label = 'This device only';
    tone = 'neutral';
  } else if (s.kind === 'needs-key') {
    icon = CloudOff;
    label = 'Connect in Settings';
    tone = 'warn';
  } else if (s.kind === 'connecting') {
    icon = RefreshCw;
    label = 'Connecting';
    tone = 'neutral';
  } else if (s.kind === 'offline') {
    icon = CloudOff;
    label = s.pending ? `Offline · ${s.pending} to send` : 'Offline';
    tone = 'warn';
  } else if (s.kind === 'error') {
    icon = CloudOff;
    label = 'Sync problem';
    tone = 'bad';
  }
  const Icon = icon;
  return (
    <a href="#/settings/sync" className={`sync-dot tone-${tone}`} title={label} aria-label={`Sync: ${label}`}>
      <Icon aria-hidden />
      <span className="sync-label">{label}</span>
    </a>
  );
}

/** Tap to switch who is logging on this device. */
function PersonSwitch() {
  const household = useHousehold();
  const device = useDevice();
  const people = household.people;
  const presenceRecords = useCollection('presence');
  const presence = new Map(presenceRecords.map((r) => [r.id, r.state]));
  return (
    <div className="person-switch" role="radiogroup" aria-label="Who is logging">
      {people.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={device.personId === p.id}
          className={device.personId === p.id ? 'on' : ''}
          onClick={() => setDevicePrefs({ personId: device.personId === p.id ? undefined : p.id })}
          title={`Logging as ${p.name}`}
        >
          <span className="avatar-wrap">
            <Avatar person={p} size={34} />
            {presence.get(p.id) && <i className={`presence-dot ${presence.get(p.id)}`} title={presence.get(p.id) === 'home' ? `${p.name} is home` : `${p.name} is out`} />}
          </span>
          <span className="person-name">{p.name}</span>
        </button>
      ))}
    </div>
  );
}
